import React, { useState, useEffect, useRef } from "react"
import Icon from "@kit/design/icon"
import { useEditingContext } from "../../data/form-data"
import { useBuilderState } from "app/forms/builder/builder-context"
import { Caption } from "./grid-gallery"

const FEATURED_THUMBNAIL_WIDTH = "150px"
const FEATURED_IMAGE_HEIGHT = "70%"

function FeaturedThumbnail({
  element_id,
  src,
  alt,
  focus,
  active,
  settings: { radius }
}) {
  const { setActiveElement } = useBuilderState()

  const containerStyles = {
    flex: `0 0 ${FEATURED_THUMBNAIL_WIDTH}`,
    opacity: active ? 1 : 0.6
  }

  const imageStyles = {
    borderRadius: radius
  }
  const imgClasses = `absolute inset-0 h-full w-full object-cover object-center hover:opacity-100 transition-opacity duration-200 ${
    active ? "opacity-100" : "opacity-75"
  }`

  const handleClick = () => {
    setActiveElement(element_id)
    focus()
  }

  return (
    <div className="mr-4" style={containerStyles}>
      <button
        onClick={handleClick}
        className="block relative w-full h-0 cursor-pointer focus:outline-none focus:gallery"
        style={{ paddingBottom: FEATURED_IMAGE_HEIGHT }}
      >
        <img src={src} alt={alt} style={imageStyles} className={imgClasses} />
      </button>
    </div>
  )
}

function FeaturedUploader({ settings: { radius }, addImage }) {
  const widthStyles = { flex: `0 0 ${FEATURED_THUMBNAIL_WIDTH}` }
  const heightStyles = { paddingBottom: FEATURED_IMAGE_HEIGHT }
  const buttonStyles = { borderRadius: radius }
  const iconStyles = { width: 20, height: 20 }

  return (
    <div style={widthStyles}>
      <div className="relative" style={heightStyles}>
        <button
          className="border-2 absolute inset-0 flex items-center justify-center w-full text-gray-500 border-dashed flex-col hover:text-gray-700 transition duration-200 border-gray-500 hover:border-gray-700"
          style={buttonStyles}
          onClick={addImage}
        >
          <Icon name="plus-circle" style={iconStyles} />
          <div className="mt-2 font-semibold text-sm leading-none font-sans">
            Add image
          </div>
        </button>
      </div>
    </div>
  )
}

export function FeaturedGallery({ images, settings, addImage, style }) {
  const editing = useEditingContext()
  const [active, setActive] = useState(0)
  const [fadeStyle, setFadeStyle] = useState({})
  const thumbnailContainer = useRef(null)
  const activeImage = images[active]

  const focus = index => setActive(index)

  const activeImageStyles = activeImage && {
    borderRadius: settings.radius
  }

  const fadeStyles = {
    fadeLeft: {
      WebkitMaskImage: `-webkit-gradient(linear, left center, right center,
        color-stop(0, transparent),
        color-stop(0.05, rgba(0, 0, 0, .5)),
        color-stop(0.1, black))`
    },
    fadeRight: {
      WebkitMaskImage: `-webkit-gradient(linear, right center, left center,
        color-stop(0, transparent),
        color-stop(0.05, rgba(0, 0, 0, .5)),
        color-stop(0.1, black))`
    },
    fadeBoth: {
      WebkitMaskImage: `-webkit-gradient(linear, left center, right center,
        color-stop(0, transparent),
        color-stop(0.05, rgba(0, 0, 0, .5)),
        color-stop(0.1, black),
        color-stop(0.9, black),
        color-stop(0.95, rgba(0, 0, 0, .5)),
        color-stop(1, transparent))`
    }
  }

  useEffect(() => {
    const container = thumbnailContainer.current

    const handleFade = () => {
      const { scrollLeft, scrollWidth, offsetWidth } = container
      const canScrollLeft = scrollLeft ? true : false
      const canScrollRight = scrollLeft + offsetWidth < scrollWidth

      if (canScrollLeft && canScrollRight) {
        setFadeStyle(fadeStyles.fadeBoth)
      } else if (canScrollLeft) {
        setFadeStyle(fadeStyles.fadeLeft)
      } else if (canScrollRight) {
        setFadeStyle(fadeStyles.fadeRight)
      } else {
        setFadeStyle({})
      }
    }

    handleFade()
    container.addEventListener("scroll", handleFade)
    return () => container.removeEventListener("scroll", handleFade)
  }, [])

  return (
    <div style={style}>
      {activeImage && (
        <figure className="m-0">
          <picture
            className="block h-0 relative"
            style={{ paddingBottom: FEATURED_IMAGE_HEIGHT }}
          >
            <img
              src={activeImage.src}
              alt={activeImage.alt}
              style={activeImageStyles}
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          </picture>
          <Caption element_id={activeImage.element_id} />
        </figure>
      )}
      <div className="relative">
        <div
          className="flex overflow-x-auto pt-4 pb-4"
          style={fadeStyle}
          ref={thumbnailContainer}
        >
          {images.map((image, index) => {
            return (
              <FeaturedThumbnail
                key={image.element_id}
                src={image.src}
                {...image}
                focus={() => focus(index)}
                active={index === active}
                settings={settings}
              />
            )
          })}
          {editing && (
            <FeaturedUploader settings={settings} addImage={addImage} />
          )}
        </div>
      </div>
    </div>
  )
}
